'use client'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertTriangle } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { formatTimeAgo, getRiskStatusLabel } from "@/i18n/utils"
import { labelDuplicateNames } from "@/lib/patientUtils"
import { AlertSettingsModal } from "@/components/AlertSettingsModal"
import { AlertBanner } from "@/components/AlertBanner"
import { RiskLeaderboard } from "@/components/RiskLeaderboard"
import { RealtimeDashboardCompact } from '@/components/RealtimeDashboardCompact'
import { SoundControl } from '@/components/SoundControl'
import { SomaAIChat } from '@/components/SomaAIChat'
import { PerfectAIPanel } from '@/components/PerfectAIPanel'
import { User, TrendingUp, FileText, LogOut, Settings, Users, CheckCircle } from 'lucide-react'
import Link from 'next/link'
import AuthService from '@/lib/auth'
import { useRouter } from 'next/navigation'
import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

const wards = [
  { value: '3A', label: '3A - 호흡기내과' },
  { value: '3B', label: '3B - 내분비내과' },
  { value: '4A', label: '4A - 소아청소년과' },
  { value: '4B', label: '4B - 신장내과' },
  { value: '5A', label: '5A - 혈액종양내과' },
  { value: '5B', label: '5B - 류마티스내과' },
  { value: '6A', label: '6A - 정형외과' },
  { value: '6B', label: '6B - 응급의학과' },
  { value: '7A', label: '7A - 신경과' },
  { value: '7B', label: '7B - 재활의학과' },
  { value: '8A', label: '8A - 일반외과' },
  { value: '8B', label: '8B - 정신건강의학과' },
  { value: '9ICU', label: '9ICU - 중환자의학과' },
  { value: '10VIP', label: '10VIP - VIP병동' }
]

function DashboardContent() {
  const router = useRouter()
  const [selectedWard, setSelectedWard] = useState('3A')
  const [showLogoutToast, setShowLogoutToast] = useState(false)
  // 기본 사용자 설정 (로그인 없이도 작동)
  const [user, setUser] = useState({
    username: 'admin',
    full_name: '시스템 관리자',
    department: '3A 병동',
    role: 'admin' as const
  })

  useEffect(() => {
    const loggedUser = AuthService.getUser()
    if (loggedUser && loggedUser.full_name) {
      setUser({
        username: loggedUser.username,
        full_name: loggedUser.full_name,
        department: loggedUser.department || '3A 병동',
        role: 'admin' as const
      })
    } else {
      // 로그인된 사용자가 없으면 기본 admin 사용자 정보를 localStorage에 저장
      const defaultUser = {
        id: 1,
        username: 'admin',
        email: 'admin@hospital.com',
        full_name: '시스템 관리자',
        role: 'admin' as const,
        department: '3A 병동',
        is_active: true,
        created_at: new Date().toISOString()
      }
      AuthService.setUser(defaultUser)
      AuthService.setToken('dummy-admin-token') // 기본 토큰 설정
    }
  }, [])

  const handleLogout = async () => {
    // 로그아웃 애니메이션 표시
    setShowLogoutToast(true)

    // 애니메이션 후 로그아웃 처리
    setTimeout(async () => {
      await AuthService.logout()
      router.push('/login')
    }, 1500)
  }

  const handleUserManagement = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    router.push('/admin/users')
  }

  // 병동별 환자 데이터 가져오기 (테이블용)
  const { data: patients, isLoading, error } = useQuery({
    queryKey: ['patients', selectedWard],
    queryFn: async () => {
      console.log(`Fetching data for ward: ${selectedWard}`)

      // Try new paginated API first
      try {
        const response = await fetch(`/api/v1/inference/risk?ward_id=${selectedWard}&page=1&per_page=200`)
        if (response.ok) {
          const data = await response.json()
          console.log(`Paginated API returned ${data.predictions?.length || 0} patients for ${selectedWard}`)
          return data.predictions || []
        }
      } catch (error) {
        console.log('Paginated API failed, trying legacy API:', error)
      }

      // Fallback to legacy API
      const response = await fetch(`/api/v1/inference/risk?ward_id=${selectedWard}`)
      const data = await response.json()
      console.log(`Legacy API returned ${data.predictions?.length || 0} patients for ${selectedWard}`)
      return data.predictions || []
    },
    refetchInterval: 30000
  })

  // 전체 환자 데이터 가져오기 (통계용)
  const { data: allPatientsData } = useQuery({
    queryKey: ['all-patients-realtime'],  // 실시간 모니터링과 동일한 키 사용
    queryFn: async () => {
      console.log('Fetching all patients for statistics')

      // 모든 병동 데이터 가져오기
      const allWards = ['3A', '3B', '4A', '4B', '5A', '5B', '6A', '6B', '7A', '7B', '8A', '8B', '9ICU', '10VIP']
      const allPredictions = []

      for (const ward of allWards) {
        try {
          const response = await fetch(`/api/v1/inference/risk?ward_id=${ward}`)
          if (response.ok) {
            const data = await response.json()
            if (data.predictions && data.predictions.length > 0) {
              allPredictions.push(...data.predictions)
            }
          }
        } catch (error) {
          console.error(`Failed to fetch data for ward ${ward}:`, error)
        }
      }

      console.log(`Total patients fetched: ${allPredictions.length}`)
      return {
        predictions: allPredictions,
        totalPatients: allPredictions.length,
        pagination: {
          total: allPredictions.length
        }
      }
    },
    refetchInterval: 30000 // 30초마다 업데이트 (실시간 모니터링과 동일)
  })

  // Safely get patients array - handle both direct array and object with predictions
  const patientsArray = Array.isArray(patients) ? patients :
                       (patients?.predictions && Array.isArray(patients.predictions)) ? patients.predictions : []

  // Use original patient data without labeling to match detail pages
  const labeledPatients = patientsArray

  // 전체 환자 통계 데이터
  const allPatients = Array.isArray(allPatientsData) ? allPatientsData : allPatientsData?.predictions || []
  const totalPatients = allPatients.length > 0 ? allPatients.length : 200 // 기본값 200명

  // Debug logging
  console.log('Current patients data:', patients)
  console.log('Patients array length:', patientsArray.length)
  console.log('Is loading:', isLoading)
  console.log('Error:', error)

  return (
    <div className="flex flex-col min-h-screen">
      {/* Mock Data Warning Banner */}
      <Alert className="m-4 border-yellow-200 bg-yellow-50">
        <AlertTriangle className="h-4 w-4 text-yellow-600" />
        <AlertDescription className="text-yellow-800">
          <strong>Demo 시스템 안내:</strong> 본 화면은 합성 데이터 기반 시뮬레이션입니다.
          실제 환자 정보가 아니며, H-IDEA 2025 경진대회 출품을 위한 Proof of Concept입니다.
        </AlertDescription>
      </Alert>

      {/* Dynamic Alert Banner */}
      <AlertBanner />

      {/* Header */}
      <header className="border-b">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img
              src="/soma/logo.png"
              alt="SOMA"
              aria-label="SOMA 로고"
              className="h-16 cursor-pointer"
              onClick={() => window.location.reload()}
            />
            <h1 className="text-2xl font-bold">한림농심병원 대시보드</h1>
          </div>
          <div className="flex items-center gap-2">
            <AlertSettingsModal />
            <SoundControl />
            <div className="h-8 w-px bg-border" />
            <Link href="/patients">
              <Button variant="outline" size="sm">
                <Users className="h-4 w-4 mr-2" />
                환자 관리
              </Button>
            </Link>
            <Link href="/leaderboard">
              <Button variant="outline" size="sm">
                <TrendingUp className="h-4 w-4 mr-2" />
                리더보드
              </Button>
            </Link>
            <Link href="/alerts/logs">
              <Button variant="outline" size="sm">
                <FileText className="h-4 w-4 mr-2" />
                알림 기록
              </Button>
            </Link>
            <Link href="/technical-explanation">
              <Button variant="outline" size="sm">
                <CheckCircle className="h-4 w-4 mr-2" />
                기술 설명
              </Button>
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback>
                      {user?.full_name?.charAt(0) || <User className="h-5 w-5" />}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end">
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium">{user?.full_name || user?.username}</p>
                    <p className="text-xs text-muted-foreground">{user?.department}</p>
                    <Badge variant="outline" className="w-fit">
                      {user?.role === 'admin' ? '관리자' :
                       user?.role === 'doctor' ? '의사' :
                       user?.role === 'nurse' ? '간호사' : '열람자'}
                    </Badge>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {user?.role === 'admin' && (
                  <DropdownMenuItem onClick={handleUserManagement}>
                    <Settings className="mr-2 h-4 w-4" />
                    사용자 관리
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  로그아웃
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* 로그아웃 토스트 알림 */}
      {showLogoutToast && (
        <div className="fixed top-20 right-4 z-50 animate-in slide-in-from-top-2 fade-in-0 duration-300">
          <div className="bg-white rounded-lg shadow-lg p-4 flex items-center space-x-3 border border-green-200">
            <div className="relative">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <div>
              <p className="font-medium text-gray-900">로그아웃되었습니다</p>
              <p className="text-sm text-gray-500">안전하게 로그아웃 되었습니다</p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 container mx-auto px-4 py-8">
        {/* KPI Cards - 전체 병원 통계 사용 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-medium">활성 경보</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">
                {allPatients.filter((p: any) => p.riskScore >= 0.75).length}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base font-medium">모니터링 환자 수</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{totalPatients}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base font-medium">평균 위험도</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">
                {allPatients.length > 0
                  ? (allPatients.reduce((sum: number, p: any) => sum + p.riskScore, 0) / allPatients.length * 100).toFixed(1)
                  : 0}%
              </p>
            </CardContent>
          </Card>
        </div>


        {/* Realtime Dashboard Compact */}
        <RealtimeDashboardCompact wardId={selectedWard} />

        {/* Risk Leaderboard */}
        <div className="mb-8">
          <RiskLeaderboard wardId={selectedWard} autoRefresh={true} refreshInterval={30000} />
        </div>

        {/* Ward Risk Table - Simple Version */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>
                {wards.find(w => w.value === selectedWard)?.label || selectedWard} - 환자 위험도 현황
              </CardTitle>
              <Select value={selectedWard} onValueChange={setSelectedWard}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="병동 선택" />
                </SelectTrigger>
                <SelectContent>
                  {wards.map(ward => (
                    <SelectItem key={ward.value} value={ward.value}>
                      {ward.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>병실</TableHead>
                  <TableHead>환자명</TableHead>
                  <TableHead>위험도</TableHead>
                  <TableHead>상태</TableHead>
                  <TableHead>최종 업데이트</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8">
                      데이터를 불러오는 중...
                    </TableCell>
                  </TableRow>
                ) : labeledPatients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8">
                      환자 데이터가 없습니다.
                    </TableCell>
                  </TableRow>
                ) : (
                  labeledPatients.slice(0, 20).map((patient: any) => (
                    <TableRow
                      key={patient.patientId}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => router.push(`/patients/${patient.patientId}`)}
                    >
                      <TableCell className="font-medium">
                        {patient.room}
                      </TableCell>
                      <TableCell>
                        {patient.name}
                      </TableCell>
                      <TableCell className="font-semibold">
                        {(patient.riskScore * 100).toFixed(1)}%
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            patient.riskScore >= 0.75
                              ? "destructive"
                              : patient.riskScore >= 0.5
                              ? "outline"
                              : "secondary"
                          }
                        >
                          {patient.riskScore >= 0.75 ? '위험' :
                           patient.riskScore >= 0.5 ? '주의' : '안정'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {formatTimeAgo(new Date(patient.timestamp || Date.now()))}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="mt-12 pb-8">
        <div className="border-t border-gray-200 dark:border-gray-800 pt-6">
          <div className="text-center space-y-3">
            <div className="flex items-center justify-center gap-3">
              {/* Hallym University Logo */}
              <svg className="h-10 w-10" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="50" cy="50" r="48" fill="#003C84"/>
                <path d="M30 35V65M30 50H50M50 35V65M70 35V65" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="50" cy="50" r="5" fill="white"/>
              </svg>
              <div className="text-left">
                <p className="text-sm font-bold text-gray-900 dark:text-gray-100">
                  2025 한림대학교 김재형 연구소
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Hallym University Kim Jae-hyung Research Institute
                </p>
              </div>
            </div>
            <div className="flex items-center justify-center gap-2 text-xs text-gray-500 dark:text-gray-500">
              <span>야간 진정·수면제 부작용 조기경보 시스템</span>
              <span>•</span>
              <span>AUROC 0.997 검증된 AI</span>
            </div>
          </div>
        </div>
      </footer>

      {/* SOMA AI Chat - Fixed position */}
      <SomaAIChat wardId={selectedWard} />
    </div>
  )
}

export default function Dashboard() {
  return <DashboardContent />
}
